import React from "react";
import { Link } from "react-router-dom";
import servicesData from "./ServiceData";
import { useLanguage } from "../context/LanguageContext";

const Services = () => {
  const { lang, t } = useLanguage();

  return (
    <section
      id="services"
      className="py-24 bg-[#0F4C5C] dark:bg-[#06181C] text-white overflow-hidden transition-colors duration-300"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Top Header Line */}
        <div className="flex items-center space-x-4 mb-12 lg:mb-16 opacity-70">
          <div className="h-[1px] bg-white/30 flex-grow"></div>
          <span className="text-sm tracking-widest uppercase font-semibold">{t.services.tag}</span>
        </div>

        {/* Heading & Intro */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end mb-12 gap-8">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif font-light leading-tight max-w-2xl">
            {t.services.heading}
          </h2>
          <p className="text-white/80 max-w-md text-base md:text-lg leading-relaxed">
            {t.services.desc}
          </p>
        </div>
      </div>

      {/* Cards Carousel (Infinite Marquee) - Full Screen Width */}
      <div className="relative overflow-hidden w-full group hover-pause py-4">
        <div className="flex w-max gap-6 animate-scroll-left px-6 md:px-12">
          {[...servicesData, ...servicesData].map((service, index) => (
            <div
              key={index}
              className="bg-white dark:bg-[#0B252B] rounded-2xl flex-shrink-0 w-[280px] sm:w-[320px] md:w-[350px] flex flex-col overflow-hidden border border-gray-100/10 dark:border-white/5 shadow-lg transition-colors duration-300"
            >
              {/* Image */}
              <div className="h-[200px] w-full overflow-hidden">
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                />
              </div>

              {/* Content */}
              <div className="p-6 md:p-8 flex flex-col flex-grow text-left">
                {/* Generic Icon */}
                <div className="w-10 h-10 rounded-full bg-[#f4f7fc] dark:bg-[#10373E] text-[#4d5c9c] dark:text-[#F4B942] flex items-center justify-center mb-6 transition-colors duration-300">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
                  </svg>
                </div>
                
                <h3 className="text-[#0F4C5C] dark:text-white text-xl font-semibold mb-3 transition-colors duration-300">
                  {lang === "te" ? service.titleTe || service.title : service.title}
                </h3>
                <p className="text-gray-500 dark:text-slate-300 text-sm leading-relaxed mb-6 flex-grow transition-colors duration-300">
                  {lang === "te" ? service.descTe || service.description : service.description}
                </p>

                <Link to={service.link} className="mt-auto inline-flex items-center text-sm font-semibold text-[#0F4C5C] dark:text-[#F4B942] hover:text-[#E09F3E] dark:hover:text-[#56b255] transition-colors">
                  <span>{t.services.learnMore}</span>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Bottom Action */}
        <div className="mt-12 flex justify-center">
          <Link
            to="/services"
            className="inline-flex items-center px-8 py-4 bg-white dark:bg-[#E09F3E] text-[#0F4C5C] dark:text-[#0A2E36] font-bold rounded-full hover:bg-gray-100 dark:hover:bg-[#c98e35] hover:text-[#E09F3E] dark:hover:text-[#0A2E36] transition-colors duration-300"
          >
            Explore All Our Services
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </Link>
        </div>

      </div>
      
      {/* Hide scrollbar styles for the carousel */}
      <style dangerouslySetInnerHTML={{__html: `
        .scrollbar-hide::-webkit-scrollbar {
            display: none;
        }
        .scrollbar-hide {
            -ms-overflow-style: none;
            scrollbar-width: none;
        }
      `}} />
    </section>
  );
};

export default Services;
